import type { CSSProperties } from "react";
import { CardBrandMark } from "./card-brand";
import { cardLabel } from "@libs/checkout-core";
import type { SavedCard } from "@libs/checkout-core";

/**
 * A saved Lissen card, as a chip inside the card number field: ticked, it
 * is the card that pays (the button names it); typing a number instead
 * swaps the chips for the Save with Lissen tick.
 */
export function SavedCardChip({
  card,
  selected,
  disabled,
  onToggle,
}: {
  card: SavedCard;
  selected: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      title={`Pay with your saved ${cardLabel(card)}`}
      style={{
        ...TAG,
        background: selected
          ? "color-mix(in srgb, var(--lc-accent) 12%, transparent)"
          : "transparent",
        borderColor: selected
          ? "color-mix(in srgb, var(--lc-accent) 35%, transparent)"
          : "var(--lc-input-border)",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <input
        type="checkbox"
        checked={selected}
        disabled={disabled}
        onChange={onToggle}
        aria-label={`Pay with ${cardLabel(card)}`}
        style={{ height: 14, margin: 0, width: 14 }}
      />
      <span style={{ fontWeight: 650 }}>Lissen</span>
      <CardBrandMark
        brand={card.brand}
        height={card.brand === "visa" ? 9 : 12}
      />
      <span style={{ fontWeight: 650 }}>{card.last4}</span>
    </label>
  );
}

/** The chip's place while the buyer's saved cards load. */
export function ChipShimmer() {
  return (
    <span
      aria-hidden="true"
      style={{
        ...TAG,
        background: "color-mix(in srgb, var(--lc-accent) 10%, transparent)",
        borderColor: "transparent",
        gap: 8,
      }}
    >
      <style>{`@keyframes lc-chip-shimmer{0%{background-position:100% 0}100%{background-position:-100% 0}}`}</style>
      <span
        style={{
          border: "1px solid var(--lc-input-border)",
          borderRadius: 3,
          height: 12,
          width: 12,
        }}
      />
      <span style={{ fontWeight: 650 }}>Lissen</span>
      <span
        style={{
          animation: "lc-chip-shimmer 1.4s linear infinite",
          background:
            "linear-gradient(90deg, color-mix(in srgb, var(--lc-accent) 18%, transparent) 25%, color-mix(in srgb, var(--lc-accent) 38%, transparent) 50%, color-mix(in srgb, var(--lc-accent) 18%, transparent) 75%)",
          backgroundSize: "200% 100%",
          borderRadius: 6,
          height: 8,
          width: 64,
        }}
      />
    </span>
  );
}

/** The small bordered tag the Save tick wears, for a card already saved. */
// Compact: a full card number at the 16px floor has to fit beside it.
const TAG: CSSProperties = {
  alignItems: "center",
  border:
    "var(--lc-border-width) var(--lc-border-style) var(--lc-input-border)",
  borderRadius: 7,
  display: "inline-flex",
  flex: "none",
  fontSize: 12,
  gap: 4,
  lineHeight: 1,
  padding: "4px 6px",
  whiteSpace: "nowrap",
};

/** "Save with Lissen", small, inside the right of the card number field. */
export function SaveTick({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled: boolean;
}) {
  return (
    <label
      title="Keep this card in your Lissen wallet for next time"
      style={{
        ...TAG,
        background: checked
          ? "color-mix(in srgb, var(--lc-accent) 10%, transparent)"
          : "transparent",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        style={{ height: 14, margin: 0, width: 14 }}
      />
      Save with Lissen
    </label>
  );
}
